/**
 * The wisdom graph as fixture data (W9 §C.5).
 *
 * 12 nodes across the 7 kinds the ACL branches on (document / truth / memory / concept / topic
 * / decision / pattern) and all 3 certainties, 14 edges, and three of the twelve nodes marked
 * `hidden` — present in the team's graph, absent from every response, COUNTED never listed.
 * That is the one rule this fixture exists to prove: a UI bug that rendered a withheld node
 * would fail here before it ever reached a live workspace.
 *
 * `wisdom.path` runs BFS twice — once over every node, once over visible nodes only — so
 * `{found:false, withheld:true}` (a route exists, but only through something you cannot see)
 * is a real algorithmic outcome, not a hard-coded pair (§E-4).
 */
import { ApiError } from "~/api/errors";
import type {
  Expert,
  WisdomCoverage,
  WisdomFilters,
  WisdomNeighborhood,
  WisdomNode,
  WisdomOverview,
  WisdomPath,
  WisdomProvenance,
  WisdomSearchResult,
  WisdomTopDown,
} from "~/api/types";
import { WisdomCoverageSchema, WisdomTopDownSchema } from "~/api/types";

interface FixtureNode {
  id: string;
  kind: string;
  title: string;
  layer: string;
  certainty: string;
  hidden: boolean;
  createdAt: string;
}

interface FixtureEdge {
  from: string;
  to: string;
  rel: string;
}

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const daysAgo = (d: number) => new Date(BASE_TIME - d * 86_400_000).toISOString();

const NODES: FixtureNode[] = [
  {
    id: "doc-1",
    kind: "document",
    title: "Access review runbook",
    layer: "L1",
    certainty: "observed",
    hidden: false,
    createdAt: daysAgo(40),
  },
  {
    id: "doc-2",
    kind: "document",
    title: "Onboarding checklist",
    layer: "L1",
    certainty: "reported",
    hidden: false,
    createdAt: daysAgo(60),
  },
  {
    id: "truth-1",
    kind: "truth",
    title: "Deploys succeed after the cache warms",
    layer: "L1",
    certainty: "observed",
    hidden: false,
    createdAt: daysAgo(30),
  },
  {
    id: "truth-2",
    kind: "truth",
    title: "Cold starts spike error rate",
    layer: "L2",
    certainty: "reported",
    hidden: false,
    createdAt: daysAgo(20),
  },
  {
    id: "memory-1",
    kind: "memory",
    title: "Incident retro, 2026-07-02",
    layer: "L1",
    certainty: "observed",
    hidden: false,
    createdAt: daysAgo(50),
  },
  {
    id: "memory-2",
    kind: "memory",
    title: "Support thread on cache warmup",
    layer: "L2",
    certainty: "reported",
    hidden: false,
    createdAt: daysAgo(25),
  },
  {
    id: "concept-1",
    kind: "concept",
    title: "Cache warmup",
    layer: "L2",
    certainty: "reported",
    hidden: false,
    createdAt: daysAgo(15),
  },
  {
    id: "topic-1",
    kind: "topic",
    title: "Deployment reliability",
    layer: "L1",
    certainty: "observed",
    hidden: false,
    createdAt: daysAgo(35),
  },
  {
    id: "pattern-1",
    kind: "pattern",
    title: "Warm-before-serve",
    layer: "L3",
    certainty: "inferred",
    hidden: false,
    createdAt: daysAgo(10),
  },
  {
    id: "pattern-2",
    kind: "pattern",
    title: "Silent retry storm",
    layer: "L3",
    certainty: "inferred",
    hidden: true,
    createdAt: daysAgo(9),
  },
  {
    id: "concept-2",
    kind: "concept",
    title: "Restricted vendor incident",
    layer: "L3",
    certainty: "inferred",
    hidden: true,
    createdAt: daysAgo(8),
  },
  {
    id: "decision-1",
    kind: "decision",
    title: "Restrict access to the vendor postmortem",
    layer: "L2",
    certainty: "reported",
    hidden: true,
    createdAt: daysAgo(7),
  },
];

const EDGES: FixtureEdge[] = [
  { from: "doc-1", to: "truth-1", rel: "ABOUT" },
  { from: "doc-1", to: "memory-1", rel: "ABOUT" },
  { from: "doc-1", to: "pattern-2", rel: "ABOUT" },
  { from: "doc-1", to: "concept-2", rel: "MENTIONS" },
  { from: "doc-1", to: "decision-1", rel: "DERIVED_FROM" },
  { from: "doc-1", to: "topic-1", rel: "MENTIONS" },
  { from: "doc-2", to: "truth-2", rel: "ABOUT" },
  { from: "doc-2", to: "memory-2", rel: "AUTHORED" },
  { from: "truth-1", to: "truth-2", rel: "DERIVED_FROM" },
  { from: "concept-1", to: "topic-1", rel: "ABOUT" },
  { from: "concept-2", to: "pattern-1", rel: "MENTIONS" },
  { from: "topic-1", to: "memory-2", rel: "REFERENCES" },
  { from: "concept-1", to: "concept-2", rel: "MENTIONS" },
  { from: "decision-1", to: "concept-1", rel: "ABOUT" },
];

const byId = new Map(NODES.map((node) => [node.id, node]));
const isHidden = (id: string) => byId.get(id)?.hidden === true;

function neighborsOf(id: string, onlyVisible: boolean): { id: string; rel: string }[] {
  const out: { id: string; rel: string }[] = [];
  for (const edge of EDGES) {
    if (edge.from === id && (!onlyVisible || !isHidden(edge.to)))
      out.push({ id: edge.to, rel: edge.rel });
    if (edge.to === id && (!onlyVisible || !isHidden(edge.from)))
      out.push({ id: edge.from, rel: edge.rel });
  }
  return out;
}

function bfsPath(
  fromId: string,
  toId: string,
  onlyVisible: boolean,
): { nodes: string[]; rels: string[] } | null {
  if (onlyVisible && (isHidden(fromId) || isHidden(toId))) return null;
  if (!byId.has(fromId) || !byId.has(toId)) return null;
  const visited = new Set([fromId]);
  const queue: { id: string; path: string[]; rels: string[] }[] = [
    { id: fromId, path: [fromId], rels: [] },
  ];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;
    if (current.id === toId) return { nodes: current.path, rels: current.rels };
    for (const next of neighborsOf(current.id, onlyVisible)) {
      if (visited.has(next.id)) continue;
      visited.add(next.id);
      queue.push({
        id: next.id,
        path: [...current.path, next.id],
        rels: [...current.rels, next.rel],
      });
    }
  }
  return null;
}

function degreeOf(id: string): number {
  return EDGES.filter((edge) => edge.from === id || edge.to === id).length;
}

export interface FakeWisdomDeps {
  requireSession(): unknown;
  delay<T>(value: T): Promise<T>;
}

export function createFakeWisdom(deps: FakeWisdomDeps) {
  let searchCalls = 0;

  return {
    search: async (input: {
      query: string;
      kinds?: string[];
      k?: number;
      filters?: WisdomFilters;
    }): Promise<WisdomSearchResult> => {
      deps.requireSession();
      searchCalls += 1;
      // "one 429 on the 4th search within a test" (§C.5) — proves the UI disables the input
      // and shows one toast rather than retrying into a storm.
      if (searchCalls === 4) {
        throw new ApiError("rate-limited", 429);
      }
      if (input.query.trim().toLowerCase() === "embeddings") {
        return deps.delay({
          query: input.query,
          hits: [],
          withheld: 0,
          degraded: true,
          skippedReason: "embeddings_disabled",
        });
      }
      const q = input.query.trim().toLowerCase();
      const kinds = new Set(input.kinds);
      const visible = NODES.filter((node) => !node.hidden);
      const withheldCount = NODES.filter((node) => node.hidden && matches(node, q)).length;
      const hits = visible
        .filter((node) => matches(node, q))
        .filter((node) => kinds.size === 0 || kinds.has(node.kind))
        .filter((node) => !input.filters?.certainty || node.certainty === input.filters.certainty)
        .filter((node) => !input.filters?.layer || node.layer === input.filters.layer)
        .slice(0, input.k ?? 10)
        .map((node, index) => ({
          id: node.id,
          kind: node.kind,
          title: node.title,
          score: Number((0.97 - index * 0.05).toFixed(6)),
          scoreKind: "cosine" as const,
          layer: node.layer,
          certainty: node.certainty,
          sourceKind: null,
          provenance: {},
          createdAt: node.createdAt,
        }));
      return deps.delay({
        query: input.query,
        hits,
        withheld: withheldCount,
        degraded: false,
        skippedReason: null,
      });
    },

    node: async (input: {
      id: string;
      kind: string;
      rel?: string;
      offset?: number;
    }): Promise<WisdomNode | null> => {
      deps.requireSession();
      const found = byId.get(input.id);
      // §B.2: "absent" and "forbidden" answer the SAME shape on purpose.
      if (!found || found.kind !== input.kind || found.hidden) {
        return deps.delay(null);
      }
      const all = neighborsOf(found.id, false);
      const relGroups = new Map<string, { id: string; rel: string }[]>();
      for (const entry of all) {
        const list = relGroups.get(entry.rel) ?? [];
        list.push(entry);
        relGroups.set(entry.rel, list);
      }
      const degrees = [...relGroups.entries()].map(([rel, list]) => ({
        rel,
        totalCount: list.length,
      }));
      const edges = [...relGroups.entries()].map(([rel, list]) => {
        const visible = list.filter((entry) => !isHidden(entry.id));
        return {
          rel,
          totalCount: list.length,
          returned: visible.slice(0, 20).map((entry) => {
            const target = byId.get(entry.id);
            if (!target) throw new Error(`Unknown wisdom node ${entry.id}`);
            return {
              rel,
              direction: "out" as const,
              id: target.id,
              kind: target.kind,
              title: target.title,
              layer: target.layer,
              certainty: target.certainty,
            };
          }),
          withheld: list.length - visible.length,
          truncated: visible.length > 20,
          offset: input.offset ?? 0,
        };
      });
      return deps.delay({
        found: true as const,
        id: found.id,
        kind: found.kind,
        layer: found.layer,
        certainty: found.certainty,
        provenance: {},
        properties: { title: found.title },
        degrees,
        edges,
      });
    },

    neighbors: async (input: {
      id: string;
      kind: string;
      depth?: 1 | 2;
      rels?: string[];
    }): Promise<WisdomNeighborhood | null> => {
      deps.requireSession();
      const origin = byId.get(input.id);
      if (!origin || origin.kind !== input.kind || origin.hidden) {
        return deps.delay(null);
      }
      const depth = input.depth === 2 ? 2 : 1;
      const relFilter = new Set(input.rels?.map((r) => r.toUpperCase()));

      const frontier = new Set<string>([origin.id]);
      const reached = new Map<string, number>();
      for (let hop = 0; hop < depth; hop++) {
        const nextFrontier = new Set<string>();
        for (const id of frontier) {
          for (const entry of neighborsOf(id, false)) {
            if (relFilter.size > 0 && !relFilter.has(entry.rel)) continue;
            if (!reached.has(entry.id) && entry.id !== origin.id) {
              reached.set(entry.id, hop + 1);
              nextFrontier.add(entry.id);
            }
          }
        }
        frontier.clear();
        for (const id of nextFrontier) frontier.add(id);
      }

      const withheldIds = [...reached.keys()].filter((id) => isHidden(id));
      const visibleIds = [...reached.keys()].filter((id) => !isHidden(id));
      const nodeCap = 50;
      const nodes = visibleIds.slice(0, nodeCap).map((id) => {
        const node = byId.get(id);
        if (!node) throw new Error(`Unknown wisdom node ${id}`);
        return {
          id: node.id,
          kind: node.kind,
          title: node.title,
          layer: node.layer,
          certainty: node.certainty,
          provenance: {},
          createdAt: node.createdAt,
        };
      });
      const nodeIdSet = new Set([origin.id, ...visibleIds.slice(0, nodeCap)]);
      const edges = EDGES.filter(
        (edge) =>
          nodeIdSet.has(edge.from) &&
          nodeIdSet.has(edge.to) &&
          (relFilter.size === 0 || relFilter.has(edge.rel)),
      ).map((edge) => ({ from: edge.from, to: edge.to, rel: edge.rel }));

      return deps.delay({
        found: true as const,
        origin: { id: origin.id, kind: origin.kind, title: origin.title },
        depth,
        nodes,
        edges,
        withheld: withheldIds.length,
        nodeCap,
        fanOutCap: 20,
        matched: reached.size,
        truncated: visibleIds.length > nodeCap,
        fanOutTruncated: 0,
      });
    },

    path: async (input: {
      fromId: string;
      toId: string;
      fromKind: string;
      toKind: string;
      maxLen?: number;
      rels?: string[];
    }): Promise<WisdomPath> => {
      deps.requireSession();
      const visiblePath = bfsPath(input.fromId, input.toId, true);
      if (visiblePath) {
        return deps.delay({
          found: true,
          withheld: false,
          length: visiblePath.nodes.length - 1,
          nodes: visiblePath.nodes.map((id) => ({ id, kind: byId.get(id)?.kind ?? "" })),
          rels: visiblePath.rels,
        });
      }
      const fullPath = bfsPath(input.fromId, input.toId, false);
      if (fullPath) {
        // A route exists, but at least one hop crosses a node this caller cannot see — the
        // EXISTENCE of that connection is itself a disclosure, so the answer collapses (§E-4).
        return deps.delay({ found: false, withheld: true, length: null, nodes: null, rels: null });
      }
      return deps.delay({ found: false, withheld: false, length: null, nodes: null, rels: null });
    },

    provenance: async (input: { id: string; kind: string }): Promise<WisdomProvenance | null> => {
      deps.requireSession();
      const found = byId.get(input.id);
      if (!found || found.kind !== input.kind || found.hidden) {
        return deps.delay(null);
      }
      const chain =
        found.kind === "document"
          ? {
              store: "documents",
              documentId: found.id,
              title: found.title,
              collectionId: null,
              parentDocumentId: null,
              hydrateVia: "/api/documents.info",
            }
          : found.kind === "truth"
            ? {
                store: "run_artifacts",
                flowSlug: "market-pulse-decisions",
                runId: "run-fx-1",
                artifactPath: "decision.json",
                version: 1,
                articleId: null,
                hydrateVia: "/api/runs.artifact",
              }
            : found.kind === "memory"
              ? {
                  store: "memory_items",
                  id: found.id,
                  source: "conversation",
                  sourceRefId: null,
                  sourceUrl: null,
                  createdBy: "user-1",
                  createdAt: found.createdAt,
                  hydrateVia: "/api/memory.list",
                }
              : // Semantic nodes (concept/topic/decision/pattern) deliberately return only a COUNT,
                // never source ids — inventing a link here is exactly the mistake this fixture
                // exists to catch (§A.1 W8).
                { store: "documents", sourceDocumentCount: 2, hydrateVia: "/api/documents.info" };
      return deps.delay({
        found: true as const,
        id: found.id,
        kind: found.kind,
        layer: found.layer,
        certainty: found.certainty,
        provenance: chain,
        withheld: 0,
      });
    },

    overview: async (): Promise<WisdomOverview> => {
      deps.requireSession();
      const groups = new Map<string, number>();
      for (const node of NODES) {
        const key = `${node.kind}|${node.certainty}`;
        groups.set(key, (groups.get(key) ?? 0) + 1);
      }
      const counts = [...groups.entries()].map(([key, total]) => {
        const [kind, certainty] = key.split("|");
        return { kind: kind ?? "", certainty: certainty ?? "", total };
      });
      const topDegree = [...NODES]
        .filter((node) => !node.hidden)
        .map((node) => ({ node, degree: degreeOf(node.id) }))
        .sort((a, b) => b.degree - a.degree)
        .slice(0, 10)
        .map(({ node, degree }) => ({
          id: node.id,
          kind: node.kind,
          title: node.title,
          layer: node.layer,
          certainty: node.certainty,
          degree,
        }));
      return deps.delay({
        counts,
        total: NODES.length,
        topDegree,
        withheld: 0,
        degraded: false,
      });
    },

    topdown: async (
      input: { scope?: "workspace" | "component"; key?: string; depth?: number } = {},
    ): Promise<WisdomTopDown> => {
      deps.requireSession();
      const components = WisdomTopDownSchema.parse({
        components: [
          {
            component: "KnowIt.Api",
            kind: "code",
            files: 42,
            lastCommitAt: "2026-10-16T09:00:00Z",
            coverage: { state: "documented" },
            tempo: { days: 30, commits: 17 },
            docs: {
              total: 1,
              withheld: 2,
              more: 0,
              items: [{ id: "doc-1", title: "How the API works", kinds: ["cites"], strength: 0.9 }],
            },
            concepts: {
              total: 1,
              more: 0,
              items: [{ id: "c1", title: "Request pipeline", certainty: "inferred" }],
            },
            decisions: { total: 0, more: 0, items: [] },
            patterns: { total: 0, more: 0, items: [] },
            truths: {
              total: 1,
              more: 0,
              items: [
                {
                  id: "t1",
                  kind: "claim",
                  statement: "Requests are authorised per hop",
                  grade: 0.82,
                  weight: 0.5,
                  state: "tested",
                  claims: "c1",
                },
              ],
            },
            drives: [
              {
                component: "KnowIt.Worker",
                lag: 2,
                score: 0.41,
                engines: ["hawkes", "lagged-ci"],
                families: 2,
                halfLifeHours: 18,
                measured: true,
              },
            ],
            drivenBy: [],
          },
          {
            component: "KnowIt.Worker",
            kind: "code",
            files: 12,
            coverage: { state: "undocumented" },
            tempo: { days: 30, commits: 4 },
            docs: { total: 0, withheld: 0, more: 0, items: [] },
            concepts: { total: 0, more: 0, items: [] },
            decisions: { total: 0, more: 0, items: [] },
            patterns: { total: 0, more: 0, items: [] },
            truths: { total: 0, more: 0, items: [] },
            drives: [],
            drivenBy: [
              {
                component: "KnowIt.Api",
                lag: 2,
                score: 0.41,
                engines: ["hawkes", "lagged-ci"],
                families: 2,
                halfLifeHours: 18,
                measured: true,
              },
            ],
          },
        ].filter((c) => input.scope !== "component" || c.component === input.key),
        componentsTotal: 2,
        causality: { status: "measured", edges: 1 },
      });
      return deps.delay(components);
    },

    coverageCode: async (): Promise<WisdomCoverage> => {
      deps.requireSession();
      return deps.delay(
        WisdomCoverageSchema.parse({
          available: true,
          stale: [
            {
              documentId: "doc-1",
              title: "How the API works",
              component: "KnowIt.Api",
              daysBehind: 41,
            },
          ],
          drift: [
            {
              documentId: "doc-1",
              title: "How the API works",
              paths: [{ path: "KnowIt.Api/Old.cs", quoted: "KnowIt.Api/Old.cs" }],
            },
          ],
        }),
      );
    },

    experts: async (query: string): Promise<Expert[]> => {
      deps.requireSession();
      const q = query.trim().toLowerCase();
      const experts: Expert[] = [
        {
          userId: "user-1",
          name: "Ada Lovelace",
          areas: ["Cache warmup", "Deployment reliability"],
          documentCount: 14,
          relevance: 0.91,
        },
        {
          userId: "user-2",
          name: "Grace Hopper",
          areas: ["Incident review"],
          documentCount: 6,
          relevance: 0.62,
        },
      ];
      if (!q) {
        return deps.delay(experts);
      }
      return deps.delay(
        experts.filter((expert) => expert.areas.some((area) => area.toLowerCase().includes(q))),
      );
    },
  };
}

function matches(node: FixtureNode, q: string): boolean {
  if (!q) return true;
  return node.title.toLowerCase().includes(q) || node.kind.includes(q);
}
